import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faEnvelope, 
  faPhone, 
  faIdCard, 
  faShieldAlt,
  faKey,
  faSave,
  faEye,
  faEyeSlash,
  faLock,
  faCheckCircle,
  faTimesCircle,
  faEdit,
  faTimes,
  faCamera,
  faTrash,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { getPhotoUrl, validateImageFile, createImagePreview } from '../../utils/imageUtils';
import WebcamCapture from '../../components/WebcamCapture';

// Máscara de telefone celular: (XX)XXXXX-XXXX
const maskPhone = (value) => {
  if (!value) return '';
  let cleaned = value.replace(/\D/g, '');
  cleaned = cleaned.slice(0, 11); // Limita a 11 dígitos
  
  if (cleaned.length <= 2) {
    return cleaned.replace(/(\d{1,2})/, '($1');
  } else if (cleaned.length <= 7) {
    return cleaned.replace(/(\d{2})(\d+)/, '($1)$2');
  } else {
    return cleaned.replace(/(\d{2})(\d{5})(\d+)/, '($1)$2-$3');
  }
};

const MeuPerfil = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const [message, setMessage] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isWebcamOpen, setIsWebcamOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Estados para edição de perfil
  const [profileData, setProfileData] = useState({
    nome: '',
    email: '',
    celular: ''
  });

  // Estados para alteração de senha
  const [passwordData, setPasswordData] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    senhaAtual: false,
    novaSenha: false,
    confirmarSenha: false
  });

  // Validações de senha
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
    passwordsMatch: false
  });

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    validatePassword();
  }, [passwordData.novaSenha, passwordData.confirmarSenha]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/verify');
      setUserData(response.data.user);
      setProfileData({
        nome: response.data.user.nome || '',
        email: response.data.user.email || '',
        celular: maskPhone(response.data.user.celular || '')
      });
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      showMessage('Erro ao carregar dados do perfil', 'error');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = () => {
    const { novaSenha, confirmarSenha } = passwordData;
    
    setPasswordValidation({
      minLength: novaSenha.length >= 8,
      hasUpper: /[A-Z]/.test(novaSenha),
      hasLower: /[a-z]/.test(novaSenha),
      hasNumber: /[0-9]/.test(novaSenha),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(novaSenha),
      passwordsMatch: novaSenha === confirmarSenha && novaSenha.length > 0
    });
  };

  const isPasswordValid = () => {
    return Object.values(passwordValidation).every(value => value === true);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Aplicar máscara no campo celular
    if (name === 'celular') {
      newValue = maskPhone(value);
    }

    setProfileData({
      ...profileData,
      [name]: newValue
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      await api.put('/auth/update-profile', profileData);
      await loadUserData();
      setEditingProfile(false);
      showMessage('Perfil atualizado com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      showMessage(error.response?.data?.message || 'Erro ao atualizar perfil', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid()) {
      showMessage('Por favor, corrija os erros antes de continuar', 'error');
      return;
    }

    try {
      setSaving(true);
      await api.put('/auth/change-password', {
        senhaAtual: passwordData.senhaAtual,
        novaSenha: passwordData.novaSenha
      });
      
      setPasswordData({
        senhaAtual: '',
        novaSenha: '',
        confirmarSenha: ''
      });
      setChangingPassword(false);
      showMessage('Senha alterada com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      showMessage(error.response?.data?.message || 'Erro ao alterar senha', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar arquivo usando utilitário
    const validation = validateImageFile(file, 5);
    if (!validation.valid) {
      showMessage(validation.error, 'error');
      return;
    }

    try {
      setUploadingPhoto(true);

      // Criar preview usando utilitário
      const preview = await createImagePreview(file);
      setPhotoPreview(preview);

      // Enviar foto
      const formData = new FormData();
      formData.append('foto', file);

      await api.post('/auth/upload-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      await loadUserData();
      await refreshUser(); // Atualizar contexto global
      showMessage('Foto atualizada com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao fazer upload da foto:', error);
      showMessage(error.response?.data?.message || 'Erro ao fazer upload da foto', 'error');
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm('Deseja realmente remover sua foto?')) {
      return;
    }

    try {
      setUploadingPhoto(true);
      await api.delete('/auth/delete-photo');
      setPhotoPreview(null);
      await loadUserData();
      await refreshUser(); // Atualizar contexto global
      showMessage('Foto removida com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao remover foto:', error);
      showMessage(error.response?.data?.message || 'Erro ao remover foto', 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleWebcamCapture = async (file) => {
    try {
      setUploadingPhoto(true);

      // Criar preview
      const preview = await createImagePreview(file);
      setPhotoPreview(preview);

      // Enviar foto
      const formData = new FormData();
      formData.append('foto', file);

      await api.post('/auth/upload-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      await loadUserData();
      await refreshUser();
      showMessage('Foto capturada e atualizada com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao fazer upload da foto:', error);
      showMessage(error.response?.data?.message || 'Erro ao fazer upload da foto', 'error');
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const cancelEdit = () => {
    setEditingProfile(false);
    setProfileData({
      nome: userData?.nome || '',
      email: userData?.email || '',
      celular: maskPhone(userData?.celular || '')
    });
  };

  const cancelPasswordChange = () => {
    setChangingPassword(false);
    setPasswordData({
      senhaAtual: '',
      novaSenha: '',
      confirmarSenha: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Meu Perfil</h1>
        <p className="text-gray-600 mt-2">Gerencie suas informações pessoais e segurança</p>
      </div>

      {/* Mensagem de Feedback */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-center">
            <FontAwesomeIcon 
              icon={message.type === 'success' ? faCheckCircle : faTimesCircle} 
              className="mr-2"
            />
            {message.text}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Card de Informações Básicas */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Informações Pessoais</h2>
              {!editingProfile && (
                <button
                  onClick={() => setEditingProfile(true)}
                  className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon icon={faEdit} className="mr-2" />
                  Editar
                </button>
              )}
            </div>

            {editingProfile ? (
              <form onSubmit={handleSaveProfile}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                      Nome Completo
                    </label>
                    <input
                      type="text"
                      name="nome"
                      value={profileData.nome}
                      onChange={handleProfileChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-gray-500" />
                      E-mail
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FontAwesomeIcon icon={faPhone} className="mr-2 text-gray-500" />
                      Celular
                    </label>
                    <input
                      type="tel"
                      name="celular"
                      value={profileData.celular}
                      onChange={handleProfileChange}
                      placeholder="(00)00000-0000"
                      maxLength={14}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Cancelar
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <FontAwesomeIcon icon={faUser} className="text-gray-500 w-5" />
                  <div className="ml-3">
                    <p className="text-xs text-gray-500">Nome</p>
                    <p className="font-medium text-gray-800">{userData?.nome}</p>
                  </div>
                </div>

                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <FontAwesomeIcon icon={faEnvelope} className="text-gray-500 w-5" />
                  <div className="ml-3">
                    <p className="text-xs text-gray-500">E-mail</p>
                    <p className="font-medium text-gray-800">{userData?.email}</p>
                  </div>
                </div>

                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <FontAwesomeIcon icon={faPhone} className="text-gray-500 w-5" />
                  <div className="ml-3">
                    <p className="text-xs text-gray-500">Celular</p>
                    <p className="font-medium text-gray-800">{userData?.celular ? maskPhone(userData.celular) : 'Não informado'}</p>
                  </div>
                </div>

                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <FontAwesomeIcon icon={faIdCard} className="text-gray-500 w-5" />
                  <div className="ml-3">
                    <p className="text-xs text-gray-500">Username</p>
                    <p className="font-medium text-gray-800">{userData?.username}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Card de Alteração de Senha */}
          <div className="bg-white rounded-lg shadow-md p-6 mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Segurança</h2>
              {!changingPassword && (
                <button
                  onClick={() => setChangingPassword(true)}
                  className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon icon={faKey} className="mr-2" />
                  Alterar Senha
                </button>
              )}
            </div>

            {changingPassword ? (
              <form onSubmit={handleChangePassword}>
                <div className="space-y-4">
                  {/* Senha Atual */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Senha Atual
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.senhaAtual ? 'text' : 'password'}
                        name="senhaAtual"
                        value={passwordData.senhaAtual}
                        onChange={handlePasswordChange}
                        className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('senhaAtual')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        <FontAwesomeIcon icon={showPasswords.senhaAtual ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>

                  {/* Nova Senha */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nova Senha
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.novaSenha ? 'text' : 'password'}
                        name="novaSenha"
                        value={passwordData.novaSenha}
                        onChange={handlePasswordChange}
                        className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('novaSenha')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        <FontAwesomeIcon icon={showPasswords.novaSenha ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>

                  {/* Confirmar Senha */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Nova Senha
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirmarSenha ? 'text' : 'password'}
                        name="confirmarSenha"
                        value={passwordData.confirmarSenha}
                        onChange={handlePasswordChange}
                        className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirmarSenha')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        <FontAwesomeIcon icon={showPasswords.confirmarSenha ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>

                  {/* Requisitos de Senha */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">Requisitos da senha:</p>
                    <div className="space-y-2">
                      <PasswordRequirement 
                        met={passwordValidation.minLength} 
                        text="Mínimo de 8 caracteres" 
                      />
                      <PasswordRequirement 
                        met={passwordValidation.hasUpper} 
                        text="Pelo menos uma letra maiúscula" 
                      />
                      <PasswordRequirement 
                        met={passwordValidation.hasLower} 
                        text="Pelo menos uma letra minúscula" 
                      />
                      <PasswordRequirement 
                        met={passwordValidation.hasNumber} 
                        text="Pelo menos um número" 
                      />
                      <PasswordRequirement 
                        met={passwordValidation.hasSpecial} 
                        text="Pelo menos um caractere especial (!@#$%^&*)" 
                      />
                      <PasswordRequirement 
                        met={passwordValidation.passwordsMatch} 
                        text="As senhas coincidem" 
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    disabled={saving || !isPasswordValid()}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faLock} className="mr-2" />
                    {saving ? 'Alterando...' : 'Alterar Senha'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelPasswordChange}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Cancelar
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-center py-8">
                <FontAwesomeIcon icon={faLock} className="text-4xl text-gray-300 mb-4" />
                <p className="text-gray-600">Sua senha está protegida</p>
                <p className="text-sm text-gray-500 mt-2">
                  Última alteração: Não disponível
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Card de Perfil */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
            <div className="text-center">
              {/* Foto do Usuário */}
              <div className="relative w-32 h-32 mx-auto mb-4 group">
                <div className="w-full h-full rounded-full overflow-hidden bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg">
                  {photoPreview || userData?.foto ? (
                    <img 
                      src={photoPreview || getPhotoUrl(userData?.foto)} 
                      alt="Foto do usuário" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FontAwesomeIcon icon={faUser} className="text-4xl text-white" />
                  )}
                </div>
                
                {/* Overlay com Botões - aparece ao passar o mouse */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-full transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex gap-2">
                    <button
                      onClick={handlePhotoClick}
                      disabled={uploadingPhoto}
                      className="bg-white text-blue-600 w-8 h-8 rounded-full hover:bg-blue-50 transition-all duration-200 disabled:opacity-50 flex items-center justify-center shadow-lg transform hover:scale-110"
                      title="Enviar foto"
                    >
                      <FontAwesomeIcon icon={faCamera} className="text-xs" />
                    </button>
                    <button
                      onClick={() => setIsWebcamOpen(true)}
                      disabled={uploadingPhoto}
                      className="bg-white text-purple-600 w-8 h-8 rounded-full hover:bg-purple-50 transition-all duration-200 disabled:opacity-50 flex items-center justify-center shadow-lg transform hover:scale-110"
                      title="Capturar com webcam"
                    >
                      <FontAwesomeIcon icon={faVideo} className="text-xs" />
                    </button>
                    {(photoPreview || userData?.foto) && (
                      <button
                        onClick={handleRemovePhoto}
                        disabled={uploadingPhoto}
                        className="bg-white text-red-600 w-8 h-8 rounded-full hover:bg-red-50 transition-all duration-200 disabled:opacity-50 flex items-center justify-center shadow-lg transform hover:scale-110"
                        title="Remover foto"
                      >
                        <FontAwesomeIcon icon={faTrash} className="text-xs" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Input de Arquivo Oculto */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
                
                {uploadingPhoto && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
              
              <h3 className="text-xl font-bold text-gray-800">{userData?.nome}</h3>
              <p className="text-gray-500 text-sm mt-1">{userData?.email}</p>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Perfil</span>
                <span className="flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
                  {userData?.perfil_nome}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Nível de Acesso</span>
                <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                  {userData?.nivel_acesso ? `Nível ${userData.nivel_acesso}` : 'Não definido'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Username</span>
                <span className="text-sm font-medium text-gray-800">{userData?.username}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal da Webcam */}
      <WebcamCapture
        isOpen={isWebcamOpen}
        onClose={() => setIsWebcamOpen(false)}
        onCapture={handleWebcamCapture}
      />
    </div>
  );
};

// Componente auxiliar para requisitos de senha
const PasswordRequirement = ({ met, text }) => (
  <div className="flex items-center text-sm">
    <FontAwesomeIcon 
      icon={met ? faCheckCircle : faTimesCircle} 
      className={`mr-2 ${met ? 'text-green-500' : 'text-gray-400'}`}
    />
    <span className={met ? 'text-green-700' : 'text-gray-600'}>{text}</span>
  </div>
);

export default MeuPerfil;
